import { DateField } from '../../../../components/inputs/DateField';
import { FileField, USER_DOCUMENT_MAX_BYTES } from '../../../../components/inputs/FileField';
import { Select } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import { formatBytes } from '../../../../lib/formatBytes';
import type { RecruitmentUserOption } from '../../vacancies/vacancyTypes';
import type { EndorsementForm } from '../useEndorsementForm';

/**
 * The endorsement's four fields — ONE component for the row kebab's popup and the deliberation modal's
 * Endorsement panel. Each id is the prefix plus the server's field name in kebab case, so a rejected
 * write focuses the field it names. Remarks are a REMARKS field: full width, outside the grid.
 */
export function EndorsementFields({
  idPrefix,
  form,
  users,
  usersLoading,
  usersError,
  currentFileName,
}: {
  idPrefix: string;
  form: EndorsementForm;
  users: RecruitmentUserOption[];
  usersLoading: boolean;
  usersError: string | null;
  /** The document already filed, on a correction — a new choice replaces it. */
  currentFileName: string | null;
}) {
  const { error, saving } = form;

  return (
    <div className="flex flex-col gap-4">
      {error ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-xs text-red-700">{error.message}</p>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          id={`${idPrefix}-endorsed-by-user-id`}
          label="Endorsed by"
          required
          description={
            usersError ?? 'The account that endorsed the applicant — not necessarily whoever records it here.'
          }
          placeholder="Choose an account…"
          value={form.endorsedBy}
          onChange={(value) => form.setEndorsedBy(value || null)}
          options={users.map((u) => ({ value: u.userId, label: u.displayName }))}
          loading={usersLoading}
          disabled={saving}
          error={form.endorsedBy ? error?.fieldError('endorsedByUserId') : 'Choose who endorsed the applicant.'}
        />

        <DateField
          id={`${idPrefix}-endorsed-date`}
          label="Endorsed at"
          required
          description="The day of the endorsement — the board's own date, which may be before today."
          value={form.endorsedDate}
          onChange={form.setEndorsedDate}
          error={form.endorsedDate ? error?.fieldError('endorsedDate') : 'Enter the date of the endorsement.'}
          disabled={saving}
        />
      </div>

      <Textarea
        id={`${idPrefix}-remarks`}
        label="Remarks"
        description="Optional — notes on the endorsement."
        value={form.remarks}
        onChange={(e) => form.setRemarks(e.target.value)}
        error={error?.fieldError('remarks')}
        disabled={saving}
      />

      <FileField
        id={`${idPrefix}-attachment`}
        label="Attachment"
        description={`Optional — the endorsement document. PDF, PNG or JPEG, up to ${formatBytes(USER_DOCUMENT_MAX_BYTES)}, its type verified against the file's own leading bytes.${currentFileName ? ` Choosing one replaces ${currentFileName}.` : ''}`}
        files={form.files}
        onChange={(next) => form.setFiles(next.slice(0, 1))}
        disabled={saving}
      />
    </div>
  );
}
